import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import MemoryBookMessage from '../components/MemoryBookMessage';
import MissionNudge from '../components/MissionNudge';
import { BookOpen } from '@phosphor-icons/react';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MemoryBookMessagePage = () => {
  const { slug } = useParams();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const notFound = recoveryStatus === 'failed';
  const [entries, setEntries] = useState([]);
  const [eventObj, setEventObj] = useState(null);

  useEffect(() => {
    if (recoveryStatus === 'ready' && recoveredEvent) {
      setEventName(recoveredEvent.event_name || slug);
      setEventObj(recoveredEvent);
      if (recoveredEvent.event_date) {
        try { setEventDate(new Date(recoveredEvent.event_date).toLocaleDateString('en-GB')); } catch { /* ignore */ }
      }
    }
    // Public count — no auth
    fetch(`${API}/events/${slug}/guestbook/entries`).then(r => r.ok ? r.json() : { entries: [] })
      .then(d => setEntries(d.entries || [])).catch(() => {});
  }, [recoveryStatus, recoveredEvent, slug]);

  if (notFound) {
    return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  }
  if (!eventObj) {
    return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;
  }

  return (
    <div className="min-h-screen bg-[#0F1014] text-white px-5 py-10" data-testid="memory-book-message-page">
      <Toaster theme="dark" position="top-center" richColors />
      <div style={{ position: 'absolute', top: 18, left: 18, zIndex: 60, paddingTop: 'env(safe-area-inset-top)' }}>
        <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="memory-book-back-btn" />
      </div>
      <MissionNudge event={eventObj} actions={['guestbook_entry']} />
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <div
            className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <BookOpen size={30} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-black mb-1">
            {eventObj?.type === 'prom' ? '🎓 School Leavers Prom' : 'Memory Guest Book'}
          </h1>
          <p className="text-gray-400 text-sm">
            {eventObj?.type === 'prom'
              ? <>Leave your final messages for <span className="text-white font-semibold">{eventName}</span></>
              : <>Leave a message for <span className="text-white font-semibold">{eventName}</span></>
            }
          </p>
          {eventDate && <p className="text-gray-600 text-xs mt-1">{eventDate}</p>}
        </div>

        <MemoryBookMessage slug={slug} eventType={eventObj?.type} onSubmitted={() => {
          // Engagement loop: mark this experience completed so the
          // GuestLanding/GuestHome CompletionBar reflects progress
          // when the guest navigates back. Dispatch a window event so
          // a same-tab CompletionBar instance also re-renders.
          try {
            import('../lib/features').then(({ markFeatureComplete }) => {
              markFeatureComplete(slug, 'memory_guestbook');
              window.dispatchEvent(new CustomEvent('eventdrop:completion_tick'));
            });
          } catch {}
          // refresh count after submission
          fetch(`${API}/events/${slug}/guestbook/entries`).then(r => r.ok ? r.json() : { entries: [] })
            .then(d => setEntries(d.entries || [])).catch(() => {});
        }} />

        <div className="mt-6 text-center text-[11px] text-gray-500">
          {entries.length > 0
            ? <>Already <span className="text-white font-semibold">{entries.length}</span> messages in the book · Powered by eventdrop.co</>
            : <>Be the first to sign · Powered by eventdrop.co</>
          }
        </div>

        {entries.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="mb-page-entries">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold">Recent messages</p>
            {entries.slice(0, 12).map(e => (
              <div key={e.entry_id} className="rounded-xl border border-white/10 bg-[#1B1C22] p-3" data-testid={`mb-entry-${e.entry_id}`}>
                <div className="text-white/70 text-xs mb-1 flex items-center justify-between">
                  <span className="font-semibold text-white">{e.guest_name}</span>
                  <span className="text-white/40 text-[10px]">{new Date(e.created_at).toLocaleDateString('en-GB')}</span>
                </div>
                {e.entry_type === 'drawn' ? (
                  <div className="text-white/40 text-[11px] italic">✍️ signed a message</div>
                ) : (
                  <p className="text-white/80 text-sm whitespace-pre-wrap line-clamp-4">{e.message_text}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryBookMessagePage;
